import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * The factory's own average timings.
 *
 * Their numbers only. The cross-supplier comparison lives behind a different
 * database function, not behind a filter here — a factory reading how it ranks
 * against the others TAP uses is a commercial disclosure, and the way that
 * happens by accident is one query with an audience branch inside it.
 *
 * Showing them at all is the point of measuring. A figure that only TAP can
 * see creates no pressure; it arrives once, in an email, as a complaint. A
 * figure the factory can watch is one they can move.
 */

interface OwnTimings {
  jobs: number;
  jobs_estimated: number;
  days_to_acknowledge: number | null;
  jobs_acknowledged: number;
  days_to_sample: number | null;
  jobs_sampled: number;
  days_to_ship: number | null;
  jobs_shipped: number;
  days_to_ready: number | null;
  jobs_ready: number;
  agreed_sample_days: number | null;
  agreed_production_days: number | null;
  days_end_to_end: number | null;
  jobs_completed: number;
}

async function fetchOwnTimings(
  client: SupabaseClient,
  organizationId: string,
): Promise<OwnTimings | null> {
  const { data, error } = await client.rpc('supplier_own_scorecard', { p_org: organizationId });
  if (error) {
    throw new Error(error.message);
  }
  return data[0] ?? null;
}

export function SupplierOwnTimings({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const timings = useQuery({
    queryKey: ['supplier-own-timings', organizationId],
    queryFn: () => fetchOwnTimings(client, organizationId),
    enabled: organizationId !== '',
    retry: false,
  });

  if (organizationId === '' || timings.isPending) {
    return <Skeleton lines={2} label="Loading your timings" />;
  }
  // Quiet on failure rather than an error box. This panel is informational and
  // sits under the board; a red banner here would read as something being
  // wrong with the jobs above it.
  if (timings.isError || timings.data === null || timings.data === undefined) {
    return null;
  }

  const row = timings.data;
  const legs: { label: string; days: number | null; jobs: number; agreed: number | null }[] = [
    {
      label: 'Accepting a PO',
      days: row.days_to_acknowledge,
      jobs: row.jobs_acknowledged,
      agreed: null,
    },
    {
      label: 'Making samples',
      days: row.days_to_sample,
      jobs: row.jobs_sampled,
      agreed: row.agreed_sample_days,
    },
    {
      // To ready for collection. What happens after that is TAP arranging a
      // courier and is not the factory's time.
      label: 'Building & ready',
      days: row.days_to_ready,
      jobs: row.jobs_ready,
      agreed: row.agreed_production_days,
    },
    {
      label: 'PO to doorstep',
      days: row.days_end_to_end,
      jobs: row.jobs_completed,
      agreed: null,
    },
  ];

  return (
    <div>
      <p className="job-order-muted">
        Your average, in days, across every job TAP has sent you. TAP sees the same figures.
      </p>
      <ul className="own-timings">
        {legs.map((leg) => (
          <li key={leg.label}>
            <span className="own-timings-label">{leg.label}</span>
            {/* An em dash, not a zero. No job has finished this leg yet, and
                "0 days" is a real and very different claim. */}
            <span className="own-timings-days">
              {leg.days === null || leg.jobs === 0 ? '—' : `${String(leg.days)} d`}
            </span>
            {leg.agreed === null ? null : (
              <span className="own-timings-n">vs {leg.agreed} agreed</span>
            )}
            <span className="own-timings-n">
              {leg.jobs === 1 ? '1 job' : `${String(leg.jobs)} jobs`}
            </span>
          </li>
        ))}
      </ul>
      {row.jobs_estimated === 0 ? null : (
        <p className="job-order-muted">
          {row.jobs_estimated === 1 ? '1 job uses' : `${String(row.jobs_estimated)} jobs use`} dates
          reconstructed from before stage tracking existed, so they read longer than they were.
        </p>
      )}
    </div>
  );
}
